// Shared shell for the three unauthenticated pages (log in, sign up, forgot
// password).
//
// It exists so those pages cannot drift apart: before this, login and signup
// were each a bare centred shadcn card, which is why the front door looked like
// an unbranded template. One shell means a brand change lands in one file.
//
// The right-hand panel is deliberately content, not decoration. Someone landing
// here may not know what Bloxx does, and the product's actual claim (pages that
// are readable by agents by construction) is more use to them than a gradient.

import { Link } from "react-router-dom";
import { Bot, FileCode2, Search } from "lucide-react";

const POINTS = [
  {
    icon: Bot,
    title: "Agent-readable by construction",
    body: "Every page ships with an MCP server, agent skills and machine-readable views. No plugins, no per-site setup.",
  },
  {
    icon: FileCode2,
    title: "One page, four formats",
    body: "Publish once and it is served as HTML, Markdown, JSON and JSON-LD, with the right headers to find them.",
  },
  {
    icon: Search,
    title: "Measured, not asserted",
    body: "Every site is scored against a public agent-readiness benchmark, so the claim is checkable.",
  },
];

export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Form side */}
      <div className="flex flex-col px-6 py-8 sm:px-10">
        <Link to="/login" className="flex items-center gap-2 self-start">
          <img
            src="https://res.cloudinary.com/dqa47jizr/image/upload/q_auto/f_auto/v1777471110/bloxx-logo_gedexe.png"
            alt=""
            className="h-7 w-7 rounded"
          />
          <span className="text-base font-semibold tracking-tight">Bloxx</span>
        </Link>

        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-sm">
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            {subtitle && (
              <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>
            )}
            <div className="mt-7">{children}</div>
            {footer && <div className="mt-6 text-sm text-muted-foreground">{footer}</div>}
          </div>
        </div>

        {/* Privacy and terms are linked from every signed-out page: Google's OAuth review checks the app links its policy. */}
        <p className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>&copy; {new Date().getFullYear()} Bloxx</span>
          <a className="underline-offset-2 hover:underline" href="https://itsbloxx.com/privacy" target="_blank" rel="noreferrer">Privacy policy</a>
          <a className="underline-offset-2 hover:underline" href="https://itsbloxx.com/terms" target="_blank" rel="noreferrer">Terms</a>
        </p>
      </div>

      {/* Brand side. Hidden below lg: on a phone it would push the form itself
          below the fold, and the form is the reason anyone is on this page. */}
      <div className="relative hidden overflow-hidden bg-brand-ink p-12 text-brand-ink-foreground lg:flex lg:flex-col lg:justify-center">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-accent/25 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-accent/10 blur-3xl"
        />

        <div className="relative max-w-md">
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-accent">
            The agent-ready CMS
          </p>
          <h2 className="mt-4 text-3xl font-semibold leading-tight tracking-tight">
            Publish once. Readable by people, search engines and agents.
          </h2>

          <ul className="mt-10 space-y-6">
            {POINTS.map((p) => {
              const Icon = p.icon;
              return (
                <li key={p.title} className="flex gap-3.5">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-ink-foreground/10 ring-1 ring-inset ring-brand-ink-foreground/15">
                    <Icon className="h-4 w-4 text-accent" />
                  </span>
                  <div>
                    <p className="text-sm font-medium">{p.title}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-brand-ink-foreground/65">
                      {p.body}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
